/*
  MA · Motion layer — micro-interactions layered on top of ma-reveal.js.
  Keys off .ma-section.is-in / .ma-reveal.is-in (set by ma-reveal.js) or its
  own data-ma-* markers, so any section gets these for free. No-ops entirely
  under prefers-reduced-motion where the effect is purely decorative.
*/
(() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- 1. FAQ: animated open/close + icon rotation ---------- */
  function initFaq(root) {
    root.querySelectorAll('.ma-faq__item').forEach((d) => {
      if (d.dataset.maFaqInit) return;
      d.dataset.maFaqInit = '1';
      const summary = d.querySelector('.ma-faq__summary');
      const answer = d.querySelector('.ma-faq__answer');
      const icon = d.querySelector('.ma-faq__icon');
      if (!summary || !answer) return;

      const setIcon = (open) => { if (icon) icon.style.transform = open ? 'rotate(180deg)' : ''; };
      setIcon(d.open);

      let interrupted = false;
      const toggle = (opening) => {
        interrupted = false;
        if (reduced) { d.open = opening; setIcon(opening); return; }
        const from = answer.getBoundingClientRect().height;
        if (opening) d.open = true;
        const to = opening ? answer.scrollHeight : 0;
        d.style.overflow = 'hidden';
        answer.style.height = from + 'px';
        answer.getBoundingClientRect(); // force reflow
        const anim = answer.animate(
          [{ height: from + 'px' }, { height: to + 'px' }],
          { duration: opening ? 420 : 320, easing: 'cubic-bezier(.32,.72,0,1)' } // the site's sheet curve (ma-tokens --ma-ease-sheet)
        );
        anim.onfinish = () => {
          if (interrupted) return;
          answer.style.height = '';
          d.style.overflow = '';
          if (!opening) d.open = false;
        };
        setIcon(opening);
      };
      d._maFaqToggle = (opening) => { interrupted = true; toggle(opening); };

      summary.addEventListener('click', (e) => {
        e.preventDefault();
        const opening = !d.open;
        const name = d.getAttribute('name');
        if (opening && name) {
          document.querySelectorAll(`details[name="${CSS.escape(name)}"]`).forEach((other) => {
            if (other !== d && other.open) {
              if (other._maFaqToggle) other._maFaqToggle(false);
              else other.open = false;
            }
          });
        }
        toggle(opening);
      });
    });
  }

  /* ---------- 2. Hex tiles: ripple in/out from cluster centre ---------- */
  function layoutHexRipple(root) {
    root.querySelectorAll('.ma-benefits-hex__cluster').forEach((cluster) => {
      const cells = [...cluster.querySelectorAll('.ma-benefits-hex__cell')];
      if (!cells.length) return;
      const cRect = cluster.getBoundingClientRect();
      const cx = cRect.width / 2, cy = cRect.height / 2;
      let max = 0;
      const dists = cells.map((cell) => {
        const r = cell.getBoundingClientRect();
        const dx = (r.left - cRect.left + r.width / 2) - cx;
        const dy = (r.top - cRect.top + r.height / 2) - cy;
        const dist = Math.hypot(dx, dy);
        max = Math.max(max, dist);
        return dist;
      });
      cells.forEach((cell, i) => {
        const delay = max > 0 ? (dists[i] / max) * 0.35 : 0;
        cell.style.setProperty('--ma-delay', delay.toFixed(3) + 's');
      });
    });
  }

  /* ---------- 3. Counter roll-up ---------- */
  function parseCount(text) {
    const m = text.match(/^(\D*)([\d,]*\d(?:\.\d+)?)(.*)$/s);
    if (!m) return null;
    const [, prefix, numStr, suffix] = m;
    const num = parseFloat(numStr.replace(/,/g, ''));
    if (isNaN(num)) return null;
    const decimals = (numStr.split('.')[1] || '').length;
    const hasComma = numStr.indexOf(',') !== -1;
    const format = (v) => {
      let s = v.toFixed(decimals);
      if (hasComma) s = s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return prefix + s + suffix;
    };
    return { num, format };
  }

  function initCounters(root) {
    const sel = '[data-ma-count], .ma-iwt__stat-text, .ma-before-after__stat, [class*="__percent"], [class*="__number"]';
    root.querySelectorAll(sel).forEach((el) => {
      if (el.dataset.maCountInit) return;
      const parsed = parseCount(el.textContent.trim());
      // single digits ("1 tree") would flash 0 before landing; nothing worth counting, leave as rendered
      if (!parsed || parsed.num < 10) return;
      el.dataset.maCountInit = '1';

      let raf = null;
      const run = () => {
        if (reduced) { el.textContent = parsed.format(parsed.num); return; }
        cancelAnimationFrame(raf);
        const start = performance.now();
        const dur = 1200;
        const step = (t) => {
          const p = Math.min((t - start) / dur, 1);
          const eased = 1 - Math.pow(1 - p, 3);
          el.textContent = parsed.format(parsed.num * eased);
          if (p < 1) raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
      };

      const section = el.closest('.ma-section');
      if (!section) { run(); return; }
      new IntersectionObserver((entries) => {
        entries.forEach((e) => { if (e.isIntersecting) run(); });
      }, { threshold: 0.2 }).observe(section);
    });
  }

  /* ---------- 4. Heading word split ---------- */
  function splitHeadingWords(root) {
    root.querySelectorAll('.ma-section h1.ma-reveal, .ma-section h2.ma-reveal').forEach((h) => {
      if (h.dataset.maSplit) return;
      const bad = [...h.children].some((c) => !['BR', 'EM', 'STRONG'].includes(c.tagName));
      if (bad) return;
      h.dataset.maSplit = '1';
      h.setAttribute('aria-label', h.textContent.trim());

      const walk = (node) => {
        [...node.childNodes].forEach((n) => {
          if (n.nodeType === 3) {
            const frag = document.createDocumentFragment();
            n.textContent.split(/(\s+)/).forEach((part) => {
              if (part === '') return;
              if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(part)); return; }
              const span = document.createElement('span');
              span.className = 'ma-word';
              span.textContent = part;
              frag.appendChild(span);
            });
            n.replaceWith(frag);
          } else if (n.nodeType === 1) {
            walk(n);
          }
        });
      };
      walk(h);
      h.querySelectorAll('.ma-word').forEach((w, i) => w.style.setProperty('--ma-word-i', Math.min(i, 20)));
    });
  }

  /* ---------- 5. Parallax ---------- */
  function initParallax() {
    if (reduced) return;
    const imgs = [...document.querySelectorAll('.ma-iwt__bg img, .ma-hero__bg img, [data-ma-parallax] img')]
      .filter((img) => !img.dataset.maParallaxInit);
    if (!imgs.length) return;
    const items = imgs.map((img) => {
      img.dataset.maParallaxInit = '1';
      img.classList.add('ma-parallax-img');
      return { img, section: img.closest('.ma-section'), active: null };
    });
    let ticking = false;
    const update = () => {
      items.forEach((it) => {
        const active = it.section ? it.section.classList.contains('is-in') : true;
        if (active !== it.active) {
          it.active = active;
          it.img.style.willChange = active ? 'transform' : '';
        }
        const rect = (it.section || it.img).getBoundingClientRect();
        const mid = rect.top + rect.height / 2 - innerHeight / 2;
        // scale(1.15) leaves 7.5% of the height free on each side; never shift past it
        const limit = rect.height * 0.028;
        const shift = active ? Math.max(-limit, Math.min(limit, mid * -0.15)) : 0;
        it.img.style.transform = `scale(1.06) translateY(${shift.toFixed(1)}px)`;
      });
      ticking = false;
    };
    addEventListener('scroll', () => {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  }

  /* ---------- 6. Before/after auto-sweep ---------- */
  function initBeforeAfterSweep(root) {
    root.querySelectorAll('ma-before-after-slider').forEach((el) => {
      if (el.dataset.maSweepInit || reduced) return;
      el.dataset.maSweepInit = '1';
      const range = el.querySelector('.ma-before-after__range');
      const section = el.closest('.ma-section');
      if (!range || !section) return;

      let interacted = false;
      let done = false;
      ['pointerdown', 'keydown', 'touchstart'].forEach((ev) =>
        range.addEventListener(ev, () => { interacted = true; }, { once: true, passive: true })
      );

      const sweep = () => {
        const stops = [50, 25, 75, 50];
        const dur = 2400 / (stops.length - 1);
        let i = 0;
        const legStart = () => {
          if (interacted || i >= stops.length - 1) return;
          const from = stops[i], to = stops[i + 1];
          const start = performance.now();
          const tick = (t) => {
            if (interacted) return;
            const p = Math.min((t - start) / dur, 1);
            range.value = from + (to - from) * p;
            range.dispatchEvent(new Event('input'));
            if (p < 1) requestAnimationFrame(tick);
            else { i++; legStart(); }
          };
          requestAnimationFrame(tick);
        };
        legStart();
      };

      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !done && !interacted) {
            done = true;
            io.disconnect();
            sweep();
          }
        });
      }, { threshold: 0.4 });
      io.observe(section);
    });
  }

  /* ---------- 7. Product carousel: drag, drift, tilt ---------- */
  function initCarousel(root) {
    root.querySelectorAll('.ma-product-carousel__grid, .ma-ritual-grid__grid').forEach((grid) => {
      if (grid.dataset.maCarouselInit) return;
      grid.dataset.maCarouselInit = '1';
      const section = grid.closest('.ma-section');

      // Figma mobile: dot pagination (one dot per card, active = nearest snapped card)
      const cards = grid.querySelectorAll('.ma-pcard');
      if (cards.length > 1 && !grid.parentElement.querySelector('.ma-product-carousel__dots')) {
        const dots = document.createElement('div');
        dots.className = 'ma-product-carousel__dots';
        cards.forEach((c, i) => {
          const d = document.createElement('button');
          d.type = 'button'; d.className = 'ma-product-carousel__dot' + (i === 0 ? ' is-active' : '');
          d.setAttribute('aria-label', 'Go to product ' + (i + 1));
          d.addEventListener('click', () => grid.scrollTo({ left: c.offsetLeft - grid.offsetLeft, behavior: 'smooth' }));
          dots.appendChild(d);
        });
        grid.insertAdjacentElement('afterend', dots);
        const sync = () => {
          const step = cards[1].offsetLeft - cards[0].offsetLeft || 1;
          const i = Math.min(cards.length - 1, Math.max(0, Math.round(grid.scrollLeft / step)));
          dots.querySelectorAll('.ma-product-carousel__dot').forEach((d, k) => d.classList.toggle('is-active', k === i));
        };
        grid.addEventListener('scroll', sync, { passive: true });
      }

      // Pointer capture only starts once the mouse has actually dragged (>5px):
      // capturing on pointerdown retargets the click to the grid, which made
      // the card links/buttons unclickable.
      let isDown = false, dragged = false, startX = 0, startScroll = 0, vx = 0, lastX = 0, lastT = 0, momentumId = null;
      grid.addEventListener('pointerdown', (e) => {
        if (e.pointerType && e.pointerType !== 'mouse') return;
        isDown = true; dragged = false;
        startX = e.clientX; startScroll = grid.scrollLeft; lastX = e.clientX; lastT = performance.now(); vx = 0;
        cancelAnimationFrame(momentumId);
      });
      grid.addEventListener('dragstart', (e) => e.preventDefault());
      grid.addEventListener('click', (e) => { if (dragged) { e.preventDefault(); e.stopPropagation(); dragged = false; } }, true);
      grid.addEventListener('pointermove', (e) => {
        if (!isDown) return;
        if (!dragged) {
          if (Math.abs(e.clientX - startX) < 6) return;
          dragged = true;
          grid.classList.add('ma-dragging');
          try { grid.setPointerCapture(e.pointerId); } catch (err) {}
        }
        grid.scrollLeft = startScroll - (e.clientX - startX);
        const now = performance.now();
        const dt = now - lastT || 16;
        vx = (e.clientX - lastX) / dt;
        lastX = e.clientX; lastT = now;
      });
      // endless: dragging/swiping on past either end loops round (ma-loop.js dissolve + cue)
      const wrap = (dir) => {
        const max = grid.scrollWidth - grid.clientWidth;
        if (max <= 0) return;
        const n = cards.length;
        if (window.MaLoop) window.MaLoop.jump(grid, { left: dir > 0 ? 0 : max, host: grid.parentElement, text: dir > 0 ? `1 / ${n}` : `${n} / ${n}` });
        else grid.scrollTo({ left: dir > 0 ? 0 : max, behavior: 'smooth' });
      };
      window.MaLoop && window.MaLoop.edges(grid, { next: () => wrap(1), prev: () => wrap(-1) });
      const endDrag = () => {
        if (!isDown) return;
        isDown = false;
        grid.classList.remove('ma-dragging');
        const span = grid.scrollWidth - grid.clientWidth;
        if (dragged && lastX - startX < -40 && startScroll >= span - 2) return wrap(1);
        if (dragged && lastX - startX > 40 && startScroll <= 2) return wrap(-1);
        if (reduced) return;
        let v = vx * -16;
        const decay = () => {
          v *= 0.94;
          grid.scrollLeft += v;
          momentumId = Math.abs(v) > 0.5 ? requestAnimationFrame(decay) : null;
        };
        if (Math.abs(v) > 0.5) momentumId = requestAnimationFrame(decay);
      };
      grid.addEventListener('pointerup', endDrag);
      grid.addEventListener('pointercancel', endDrag);
      grid.addEventListener('pointerleave', () => { if (isDown) endDrag(); });

      let hovered = false, pointerActive = false, focused = false;
      grid.addEventListener('mouseenter', () => { hovered = true; });
      grid.addEventListener('mouseleave', () => { hovered = false; });
      grid.addEventListener('pointerdown', () => { pointerActive = true; });
      addEventListener('pointerup', () => { pointerActive = false; });
      grid.addEventListener('focusin', () => { focused = true; });
      grid.addEventListener('focusout', () => { focused = false; });

      if (!reduced) {
        // Float accumulator: scrollLeft truncates sub-pixel writes, so += 0.3
        // alone never moves. At the end it loops round to the start (no ping-pong).
        let pos = grid.scrollLeft, lastSeen = grid.scrollLeft;
        // R4 speed: the loop used to run every frame forever (off-screen too) and re-measure layout each
        // frame — the biggest main-thread cost on mobile. Now it only runs while the grid is on screen and
        // the scroll range is measured on resize.
        let max = 0, onScreen = false, running = false;
        const measure = () => { max = grid.scrollWidth - grid.clientWidth; };
        new ResizeObserver(measure).observe(grid);
        measure();
        const drift = () => {
          if (!onScreen || max <= 0) { running = false; return; }
          if (!hovered && !pointerActive && !focused && !isDown && momentumId === null && !grid._maJump) {
            if (Math.abs(grid.scrollLeft - lastSeen) > 1) pos = grid.scrollLeft; // user/arrow moved it
            pos = Math.min(max, Math.max(0, pos + 0.3));
            if (pos >= max) { wrap(1); pos = 0; lastSeen = 0; }
            else { grid.scrollLeft = pos; lastSeen = grid.scrollLeft; }
          }
          requestAnimationFrame(drift);
        };
        new IntersectionObserver(([e]) => {
          onScreen = e.isIntersecting;
          if (onScreen && !running) { running = true; measure(); requestAnimationFrame(drift); }
        }).observe(grid);

        // ponytail: card tilt removed (client: no hover effects on product cards)
      }
    });
  }


  /* ---------- 8. Orbit (mobile): autoplay snap slider + dots + centred-node emphasis ---------- */
  function initOrbitSlider(root) {
    root.querySelectorAll('.ma-orbit__nodes').forEach((track) => {
      if (track.dataset.maOrbitInit) return;
      track.dataset.maOrbitInit = '1';
      const nodes = [...track.querySelectorAll('.ma-orbit__node')];
      if (nodes.length < 2) return;
      const mq = window.matchMedia('(max-width: 749px)');
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const dots = document.createElement('div');
      dots.className = 'ma-orbit__dots';
      nodes.forEach((n, i) => {
        const d = document.createElement('button');
        d.type = 'button'; d.className = 'ma-orbit__dot' + (i === 0 ? ' is-active' : '');
        d.setAttribute('aria-label', 'Show item ' + (i + 1));
        d.addEventListener('click', () => { go(i); rest(); });
        dots.appendChild(d);
      });
      track.insertAdjacentElement('afterend', dots);

      let cur = 0, timer = null, ticking = false;
      const centre = (i) => nodes[i].offsetLeft + nodes[i].offsetWidth / 2 - track.clientWidth / 2;
      const go = (i) => {
        const n = nodes.length;
        cur = (i + n) % n;
        // wrapping round: dissolve to the other end with the loop cue rather than rewinding
        if (cur !== i && window.MaLoop) return window.MaLoop.jump(track, { left: centre(cur), host: track.parentElement, text: `${cur + 1} / ${n}` });
        track.scrollTo({ left: centre(cur), behavior: reduce ? 'auto' : 'smooth' });
      };
      window.MaLoop && window.MaLoop.edges(track, { next: () => { go(cur + 1); rest(); }, prev: () => { go(cur - 1); rest(); } });
      const paint = () => {
        ticking = false;
        const mid = track.scrollLeft + track.clientWidth / 2;
        let best = 0, bd = Infinity;
        nodes.forEach((n, i) => {
          const d = Math.abs(n.offsetLeft + n.offsetWidth / 2 - mid);
          if (d < bd) { bd = d; best = i; }
          // fluid emphasis: 1 at centre → 0.4 one slot away
          const k = Math.max(0, 1 - d / (n.offsetWidth + 40));
          n.style.opacity = (0.4 + 0.6 * k).toFixed(3);
          n.style.transform = 'scale(' + (0.9 + 0.1 * k).toFixed(3) + ')';
        });
        cur = best;
        dots.querySelectorAll('.ma-orbit__dot').forEach((d, k) => d.classList.toggle('is-active', k === best));
      };
      track.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(paint); } }, { passive: true });

      const play = () => { if (reduce || !mq.matches || timer) return; timer = setInterval(() => go(cur + 1), 4200); };
      const stop = () => { clearInterval(timer); timer = null; };
      let restT;
      const rest = () => { stop(); clearTimeout(restT); restT = setTimeout(play, 7000); };
      ['pointerdown', 'touchstart', 'wheel'].forEach((ev) => track.addEventListener(ev, rest, { passive: true }));

      // only run while the section is on screen (and on mobile)
      const io = new IntersectionObserver((es) => es.forEach((e) => (e.isIntersecting ? play() : stop())), { threshold: 0.3 });
      io.observe(track);
      mq.addEventListener('change', () => { stop(); if (mq.matches) { paint(); play(); } else nodes.forEach((n) => { n.style.opacity = ''; n.style.transform = ''; }); });
      if (mq.matches) paint();
    });
  }

  const init = (root = document) => {
    initFaq(root);
    layoutHexRipple(root);
    initCounters(root);
    splitHeadingWords(root);
    initBeforeAfterSweep(root);
    initCarousel(root);
    initOrbitSlider(root);
  };

  init();
  initParallax();
  addEventListener('load', () => { layoutHexRipple(document); initParallax(); });
  document.addEventListener('shopify:section:load', (e) => { init(e.target); initParallax(); });
})();
